import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Two impersonation modes for app admins · both stored in sessionStorage
 * so they survive a reload but not a new browser session.
 *
 *  - PREVIEW mode (`previewing` boolean) · UI-only. Hides admin chrome
 *    so the admin sees what a regular employee sees. No backend swap.
 *
 *  - ACT-AS mode (`actAsEmail` string) · real impersonation. The axios
 *    layer sends X-Impersonate-User-Email on every request, and the
 *    Clerk auth middleware swaps `request.user` to that email when the
 *    real user is an admin. Used for testing flows that span multiple
 *    roles (submitter, manager validating, etc.).
 */

const STORAGE_PREVIEW = 'virgy.previewAsEmployee';
const STORAGE_ACT_AS  = 'virgy.actAsEmail';
const STORAGE_ACT_NAME = 'virgy.actAsName';

const Ctx = createContext({
  previewing: false,
  start: () => {},
  stop: () => {},
  toggle: () => {},
  actAsEmail: null,
  actAsName: null,
  startActingAs: () => {},
  stopActingAs: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export const useImpersonation = () => useContext(Ctx);

/* Direct accessor for the axios interceptor · avoids needing the React
 * context in plain-JS modules. Returns the current act-as email or '' . */
// eslint-disable-next-line react-refresh/only-export-components
export const getActAsEmail = () => {
  try { return sessionStorage.getItem(STORAGE_ACT_AS) || ''; } catch { return ''; }
};

export default function ImpersonationProvider({ children }) {
  const [previewing, setPreviewing] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_PREVIEW) === '1'; } catch { return false; }
  });
  const [actAsEmail, setActAsEmail] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_ACT_AS) || null; } catch { return null; }
  });
  const [actAsName, setActAsName] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_ACT_NAME) || null; } catch { return null; }
  });

  useEffect(() => {
    try {
      if (previewing) sessionStorage.setItem(STORAGE_PREVIEW, '1');
      else sessionStorage.removeItem(STORAGE_PREVIEW);
    } catch { /* ignore */ }
  }, [previewing]);

  useEffect(() => {
    try {
      if (actAsEmail) sessionStorage.setItem(STORAGE_ACT_AS, actAsEmail);
      else sessionStorage.removeItem(STORAGE_ACT_AS);
      if (actAsName) sessionStorage.setItem(STORAGE_ACT_NAME, actAsName);
      else sessionStorage.removeItem(STORAGE_ACT_NAME);
    } catch { /* ignore */ }
  }, [actAsEmail, actAsName]);

  const start  = useCallback(() => setPreviewing(true), []);
  const stop   = useCallback(() => setPreviewing(false), []);
  const toggle = useCallback(() => setPreviewing(v => !v), []);

  const startActingAs = useCallback((email, name) => {
    setActAsEmail((email || '').trim().toLowerCase() || null);
    setActAsName(name || null);
  }, []);
  const stopActingAs = useCallback(() => {
    setActAsEmail(null);
    setActAsName(null);
  }, []);

  const value = useMemo(() => ({
    previewing, start, stop, toggle,
    actAsEmail, actAsName, startActingAs, stopActingAs,
  }), [previewing, start, stop, toggle, actAsEmail, actAsName, startActingAs, stopActingAs]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
